import React, { useState } from 'react';
import { ApiClient } from '../../api/client';
import { FolderTemplateV2Button } from './FolderTemplateV2Button';
import { TemplateSamplesManager } from './TemplateSamplesManager';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import type { StagingBatch, StagingItem, StagingVariablesJson } from '../../types';
import {
  Upload, FolderArchive, FileText, CheckCircle2, AlertTriangle,
  Trash2, Edit2, Plus, ArrowRight, RefreshCw, X, Check, Download, Sparkles, FolderDown
} from 'lucide-react';

/** A ZIP-ben talált szervezet_valtozok.json eltéréseinek átvétele a szervezet változóiként */
const ZipVariablesPanel: React.FC<{ info: StagingVariablesJson }> = ({ info }) => {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(info.changes.map((c) => c.key)));
  const [pickedNew, setPickedNew] = useState<Set<string>>(() => new Set(info.unknown));
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'dismissed'>('idle');
  const [error, setError] = useState<string | null>(null);
  if (state === 'dismissed') return null;
  if (!info.sameTenant) {
    return (
      <div className="p-3 rounded-2xl text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400">
        A ZIP-ben lévő <span className="font-mono">szervezet_valtozok.json</span> egy másik szervezethez tartozik: értékeit csak cserelistaként használtuk
        (a szövegekben az ő adatai a te szervezeted adataira cserélődtek), a változóid nem módosultak.
      </div>
    );
  }
  if (!info.changes.length && !info.unknown.length) return null;
  const toggle = (set: Set<string>, k: string, apply: (s: Set<string>) => void) => { const n = new Set(set); if (n.has(k)) n.delete(k); else n.add(k); apply(n); };
  const adopt = async () => {
    setState('saving'); setError(null);
    const values: Record<string, string> = {};
    for (const c of info.changes) if (picked.has(c.key)) values[c.key] = c.proposed;
    const addCustom = info.unknown.filter((k) => pickedNew.has(k)).map((k) => ({ key: k, label: k }));
    const res = await ApiClient.saveTemplateVariables(info.tenantId, values, undefined, { merge: true, addCustom });
    if (res.ok) setState('done'); else { setError(res.error || 'Nem sikerült menteni'); setState('idle'); }
  };
  if (state === 'done') {
    return (
      <div className="p-3 rounded-2xl text-xs bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300 flex items-center gap-2" role="status">
        <CheckCircle2 className="w-4 h-4" /> A kiválasztott értékeket elmentettük a szervezet változóiként (Szervezetek → Sablon ablakban látod őket).
      </div>
    );
  }
  return (
    <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-xs space-y-2.5">
      <div className="font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" /> A ZIP szervezet_valtozok.json-ja eltér a szervezet mentett változóitól
      </div>
      <p className="text-amber-800/90 dark:text-amber-300/90">
        Ebben a betöltésben a {'{{KULCS}}'} helyekre már a json értékei kerültek. Átveheted őket tartósan is – ekkor a következő sablon-telepítés, ZIP-letöltés és -import is ezeket használja.
      </p>
      {info.changes.length > 0 && (
        <div className="rounded-xl bg-white dark:bg-slate-900 border border-amber-200/70 dark:border-amber-900/60 divide-y divide-slate-100 dark:divide-slate-800 max-h-56 overflow-y-auto">
          {info.changes.map((c) => (
            <label key={c.key} className="flex items-start gap-2 px-3 py-1.5 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={picked.has(c.key)} onChange={() => toggle(picked, c.key, setPicked)} disabled={!info.canSave} />
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-slate-800 dark:text-slate-200">{c.label}</span>{' '}
                <span className="font-mono text-[10px] text-slate-400">{c.key}</span>
                <span className="block text-slate-500 dark:text-slate-400 break-words">
                  <span className="line-through">{c.current || '(üres)'}</span> → <span className="text-emerald-700 dark:text-emerald-300 font-medium">{c.proposed || '(üres)'}</span>
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
      {info.unknown.length > 0 && (
        <div className="space-y-1">
          <div className="text-amber-900 dark:text-amber-200">Új kulcsok a json-ban (saját változóként vehetők fel):</div>
          <div className="flex flex-wrap gap-2">
            {info.unknown.map((k) => (
              <label key={k} className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900 font-mono cursor-pointer">
                <input type="checkbox" checked={pickedNew.has(k)} onChange={() => toggle(pickedNew, k, setPickedNew)} disabled={!info.canSave} /> {k}
              </label>
            ))}
          </div>
        </div>
      )}
      {error && <div className="text-rose-700 dark:text-rose-300" role="alert">{error}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {info.canSave ? (
          <button type="button" onClick={adopt} disabled={state === 'saving' || (!picked.size && !pickedNew.size)}
            className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
            {state === 'saving' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Átvétel a szervezet változóiként
          </button>
        ) : (
          <span className="text-amber-800 dark:text-amber-300">A tartós átvételhez szervezeti admin jogosultság kell.</span>
        )}
        <button type="button" onClick={() => setState('dismissed')} className="px-3 py-1.5 rounded-xl border border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200 cursor-pointer">Most nem</button>
      </div>
    </div>
  );
};

/**
 * mode='templates': „Sablonok importálása/Exportálása” – a központi sablon letöltései (+ superadmin: mintadokumentum-tár)
 * mode='own':       „Saját feltöltött adatok strukturált letöltése” – a saját adatok ZIP-je és a visszatöltés (staging)
 */
export const SmartZipImporterView: React.FC<{ mode?: 'templates' | 'own' }> = ({ mode = 'templates' }) => {
  const isOwn = mode === 'own';
  const { role } = useAuth();
  const isSuper = String(role) === 'superadmin';
  const { setActiveCategory, setActiveModule } = useLayout();
  const [samplesRev, setSamplesRev] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [stagingBatch, setStagingBatch] = useState<StagingBatch | null>(null);
  const [applyMode, setApplyMode] = useState<'UPSERT' | 'APPEND' | 'REPLACE'>('UPSERT');
  const [isApplying, setIsApplying] = useState(false);
  const [appliedResult, setAppliedResult] = useState<{ count: number; files?: number; warnings?: string[] } | null>(null);

  // In-line editing state
  const [editingItem, setEditingItem] = useState<{ pageTitle: string; item: StagingItem } | null>(null);
  const [editForm, setEditForm] = useState({ pont: '', cim: '', leiras: '', kulso_linkek: '' });

  // Add missing item state
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItemPage, setNewItemPage] = useState('');
  const [newItemForm, setNewItemForm] = useState({ pont: '', cim: '', leiras: '' });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setAppliedResult(null);

    const res = await ApiClient.uploadZipForStaging(file);
    if (res.ok && res.stagingId) {
      setStagingBatch(res as unknown as StagingBatch);
    } else {
      alert('Hiba történt a ZIP feldolgozásakor: ' + (res.error || 'Ismeretlen hiba'));
    }
    setIsUploading(false);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!stagingBatch) return;
    const res = await ApiClient.deleteStagingItem(stagingBatch.stagingId, itemId);
    if (res.ok) {
      const updatedPages = stagingBatch.pages.map(p => ({
        ...p,
        records: p.records.filter(r => r.id !== itemId),
      }));
      setStagingBatch({ ...stagingBatch, pages: updatedPages });
    }
  };

  const handleSaveEdit = async () => {
    if (!stagingBatch || !editingItem) return;
    const res = await ApiClient.updateStagingItem(stagingBatch.stagingId, editingItem.item.id, editForm);
    if (res.ok) {
      const updatedPages = stagingBatch.pages.map(p => ({
        ...p,
        records: p.records.map(r => r.id === editingItem.item.id ? { ...r, ...editForm } : r),
      }));
      setStagingBatch({ ...stagingBatch, pages: updatedPages });
      setEditingItem(null);
    }
  };

  const handleSaveNewItem = async () => {
    if (!stagingBatch || !newItemPage) return;
    const res = await ApiClient.addStagingItem(stagingBatch.stagingId, {
      pageTitle: newItemPage,
      ...newItemForm,
    });
    if (res.ok && res.item) {
      const updatedPages = stagingBatch.pages.map(p => {
        if (p.pageTitle === newItemPage) {
          return { ...p, records: [...p.records, res.item] };
        }
        return p;
      });
      setStagingBatch({ ...stagingBatch, pages: updatedPages });
      setIsAddingNew(false);
      setNewItemForm({ pont: '', cim: '', leiras: '' });
    }
  };

  const handleApply = async () => {
    if (!stagingBatch) return;
    setIsApplying(true);
    const res = await ApiClient.applyStagingBatch(stagingBatch.stagingId, applyMode);
    if (res.ok) {
      setAppliedResult({ count: res.appliedRecords || 0, files: (res as any).appliedFiles || 0, warnings: (res as any).warnings || [] });
      setStagingBatch(null);
      setFile(null);
    } else {
      alert('Hiba a véglegesítés során: ' + (res.error || 'Ismeretlen hiba'));
    }
    setIsApplying(false);
  };

  return (
    <div className="space-y-6">
      {/* Fejléc */}
      <div className="pb-5 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
          {isOwn ? <FolderDown className="w-7 h-7 text-cyan-600 dark:text-cyan-400" /> : <FolderArchive className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />}
          <span>{isOwn ? 'Saját feltöltött adatok strukturált letöltése' : 'Sablonok importálása/Exportálása'}</span>
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {isOwn ? (
            <>Itt töltheted le a szervezeted adatait a központi sablon mappaszerkezetében: minden pont mappájában a már feltöltött szövegek és fájlok.
            A gépeden kiegészítve (új fájlok, javított szövegek) ugyanitt visszatöltheted – a véglegesítés előtt előnézetben ellenőrizheted a változásokat.</>
          ) : (
            <>A jogszabályi előírásoknak megfelelő központi (2026-os) közzétételi sablon letöltése: az üres mappaszerkezet, illetve a pontokhoz tartozó minta
            dokumentumokkal (docx, xlsx …) kiegészített változat. A kitöltött mappák visszatöltése a <strong>Saját feltöltött adatok strukturált letöltése</strong> menüpontban történik.</>
          )}
        </p>
      </div>

      {/* Sikerült alkalmazás visszajelzés */}
      {appliedResult && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-300 shadow-xs">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <div className="font-bold text-sm text-emerald-900 dark:text-emerald-200">Sikeres importálás és közzététel!</div>
              <div>Összesen {appliedResult.count} bejegyzés és {appliedResult.files || 0} melléklet került véglegesítésre az adatbázisban.</div>
              {(appliedResult.warnings || []).length > 0 && (
                <details className="mt-1 text-amber-800 dark:text-amber-300">
                  <summary className="cursor-pointer">{appliedResult.warnings!.length} melléklet kimaradt – részletek</summary>
                  <ul className="list-disc ml-5 mt-1 space-y-0.5">{appliedResult.warnings!.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}</ul>
                </details>
              )}
            </div>
          </div>
          <button
            onClick={() => setAppliedResult(null)}
            className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition cursor-pointer shadow-xs"
          >
            Új feltöltés indítása
          </button>
        </div>
      )}

      {/* Mappastruktúra Sablon Generálása, Letöltése & 3 Lépéses Munkamenet */}
      {!stagingBatch && (
        <div className="space-y-4">
          {/* Fő Letöltő Központ Kártya */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-50/80 via-blue-50/50 to-cyan-50/80 dark:from-slate-900 dark:via-indigo-950/30 dark:to-slate-900 border border-indigo-200/80 dark:border-indigo-900/50 shadow-sm space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/20 shrink-0 mt-0.5">
                  <FolderArchive className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                      {isOwn ? 'A szervezet adatai a sablon mappaszerkezetében (ZIP)' : 'Központi közzétételi sablon letöltése (ZIP)'}
                    </h3>
{!isOwn && (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      101 TÖRVÉNYI PONT
                    </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1.5 max-w-2xl leading-relaxed">
                    {isOwn
                      ? 'A központi sablon pontjai a szervezet saját, már feltöltött tartalmával és mellékleteivel. Bontsd ki, egészítsd ki, tömörítsd újra, és töltsd vissza lent.'
                      : 'Az üres mappaszerkezet a 2026-os lista pontjaival, vagy ugyanez a superadmin által összeállított minta sablonfájlokkal. Bontsd ki, másold a közzéteendő dokumentumokat a megfelelő sorszámú mappákba, majd a Saját feltöltött adatok menüpontban töltsd vissza.'}
                  </p>
                </div>
              </div>

              {/* Akció gombok */}
              <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                {!isOwn && (
                <>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await ApiClient.downloadFolderTemplateZip({ blank: true });
                    } catch (e: any) {
                      alert('Hiba a sablon letöltése során: ' + (e.message || 'Ismeretlen hiba'));
                    }
                  }}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md shadow-indigo-600/20 transition flex items-center gap-2 cursor-pointer"
                  title="Üres mappastruktúra generálása a 101 jogszabályi közzétételi ponttal"
                >
                  <Download className="w-4 h-4" />
                  <span>Üres Mappa-Sablon (ZIP)</span>
                </button>

                <FolderTemplateV2Button refreshKey={samplesRev} />
                </>
                )}

                {isOwn && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await ApiClient.downloadFolderTemplateZip({ blank: false });
                    } catch (e: any) {
                      alert('Hiba az intézményi mappák letöltése során: ' + (e.message || 'Ismeretlen hiba'));
                    }
                  }}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md shadow-indigo-600/20 transition flex items-center gap-2 cursor-pointer"
                  title="A szervezet meglévő közzétételi pontjainak és mellékleteinek letöltése mappákban"
                >
                  <Download className="w-4 h-4" />
                  <span>Saját feltöltött mappastruktúra (ZIP)</span>
                </button>
                )}

              </div>
            </div>

            {/* 3 Lépéses folyamat-útmutató */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-indigo-100 dark:border-slate-800/80">
              <div className="p-3 rounded-xl bg-white/70 dark:bg-slate-800/40 border border-indigo-100/60 dark:border-slate-800 flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Letöltés & Kicsomagolás</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {isOwn
                      ? <>Kattints a <strong>Saját feltöltött mappastruktúra (ZIP)</strong> gombra: a pontok mappáiban a már feltöltött tartalom van.</>
                      : <>Kattints az <strong>Üres Mappa-Sablon (ZIP)</strong> vagy az <strong>Üres közzétételi dokumentum sablonok (ZIP)</strong> gombra. A ZIP már tartalmazza a törvényi kötelező pontokat.</>}
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-white/70 dark:bg-slate-800/40 border border-indigo-100/60 dark:border-slate-800 flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Fájlok & Változók Kitöltése</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Másold a fájlokat a mappákba. Elég a gyökérben lévő <code>szervezet_valtozok.json</code>-t kitölteni a globális cseréhez!
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-white/70 dark:bg-slate-800/40 border border-indigo-100/60 dark:border-slate-800 flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Visszatöltés</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {isOwn
                      ? 'Tömörítsd újra ZIP-be és töltsd fel lent. A rendszer feloldja a változókat, előnézetet ad, és a véglegesítéskor közzéteszi.'
                      : <>Tömörítsd újra ZIP-be, és töltsd fel a <button type="button" onClick={() => { setActiveCategory('data_hub'); setActiveModule('own_data_export'); }} className="underline font-semibold text-indigo-700 dark:text-indigo-300 cursor-pointer">Saját feltöltött adatok strukturált letöltése</button> menüpontban.</>}
                  </div>
                </div>
              </div>
            </div>

            {/* Információs sáv a DOCX/PDF kompatibilis sablonmotorról */}
            <div className="p-2.5 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-between text-[11px] text-indigo-800 dark:text-indigo-300">
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span>A sablonok és a mellékelt <strong>kozzeteteli_valtozok_schema.json</strong> szabványos Mustache / Handlebars formátumú, közvetlenül alkalmas DOCX és PDF generátorokhoz is.</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Központi mintadokumentum-tár (superadmin) */}
      {!isOwn && isSuper && <TemplateSamplesManager onChanged={() => setSamplesRev((n) => n + 1)} />}

      {/* Feltöltési Zóna */}
      {isOwn && !stagingBatch && (
        <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-cyan-500 dark:hover:border-cyan-500/60 rounded-2xl p-10 text-center bg-white dark:bg-slate-900/40 shadow-xs transition group">
          <input
            type="file"
            id="zip-upload"
            accept=".zip"
            onChange={handleFileChange}
            className="hidden"
          />
          <label htmlFor="zip-upload" className="cursor-pointer space-y-4 block">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 group-hover:bg-cyan-50 dark:group-hover:bg-cyan-500/20 text-slate-500 dark:text-slate-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 flex items-center justify-center transition shadow-xs">
              <Upload className="w-8 h-8" />
            </div>
            <div>
              <div className="font-bold text-base text-slate-900 dark:text-slate-100">
                {file ? file.name : 'Kattints ide a ZIP fájl kiválasztásához'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
                Támogatott formátum: .zip (max. 100 MB) többszintű mappákkal és mellékletekkel
              </div>
            </div>
          </label>

          {file && (
            <div className="mt-5 flex items-center justify-center gap-3">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-xs shadow-md shadow-cyan-600/20 flex items-center gap-2 transition disabled:opacity-50 cursor-pointer"
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Feldolgozás és struktúra elemzés...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Mappastruktúra elemzése és betöltése</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Interaktív Staging / Előnézeti Mátrix */}
      {isOwn && stagingBatch && (
        <div className="space-y-4">
          {stagingBatch.variablesJson && <ZipVariablesPanel key={stagingBatch.stagingId} info={stagingBatch.variablesJson} />}
          {/* Staging Vezérlősáv */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
            <div>
              <div className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <span>Staging köteg: {stagingBatch.fileName}</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 font-mono font-semibold border border-cyan-200 dark:border-cyan-800">
                  {stagingBatch.pages.reduce((acc, p) => acc + p.records.length, 0)} tétel
                </span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Vizsgáld át az importálandó tételeket. A hiányzó adatokat pótolhatod, a téveseket törölheted a véglegesítés előtt.
              </div>
              {(stagingBatch.warnings || []).length > 0 && (
                <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                  Kihagyott mellékletek: {stagingBatch.warnings!.slice(0, 5).join(' · ')}{stagingBatch.warnings!.length > 5 ? ` (+${stagingBatch.warnings!.length - 5})` : ''}
                </div>
              )}
            </div>

            <div className="flex items-center flex-wrap gap-3">
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600 dark:text-slate-400 font-medium">Stratégia:</span>
                <select
                  value={applyMode}
                  onChange={(e: any) => setApplyMode(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-800 dark:text-slate-200 font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="UPSERT">Meglévő pont frissítése pontszám alapján (cím frissül, tartalom bővül)</option>
                  <option value="APPEND">Új pontként kerüljön be (a régi megmarad)</option>
                  <option value="REPLACE">Oldal teljes felülírása (a régi pontok törlődnek)</option>
                </select>
              </div>
              {(stagingBatch.existingPointCount || 0) > 0 && (
                <span className="text-[11px] text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 px-2 py-1 rounded-lg">
                  {stagingBatch.existingPointCount} pont már létezik a szervezetnél
                </span>
              )}

              <button
                onClick={() => {
                  setNewItemPage(stagingBatch.pages[0]?.pageTitle || '');
                  setIsAddingNew(true);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold border border-slate-300 dark:border-slate-700 flex items-center gap-1.5 transition cursor-pointer shadow-xs"
              >
                <Plus className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                <span>Hiányzó tétel pótlása</span>
              </button>

              <button
                onClick={handleApply}
                disabled={isApplying}
                className="px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer"
              >
                {isApplying ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Mentés folyamatban...</span>
                  </>
                ) : (
                  <>
                    <span>Véglegesítés a Nyilvántartásba</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Oldalak és tételek előnézete */}
          {stagingBatch.pages.map((page, pIdx) => (
            <div key={pIdx} className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/60 shadow-xs">
              <div className="bg-slate-50 dark:bg-slate-950/70 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-2 font-bold text-xs text-slate-900 dark:text-slate-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-cyan-500"></span>
                  <span>{page.pageTitle}</span>
                  {page.isNewPage ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-mono font-semibold">
                      Új oldal
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-mono font-semibold">
                      Meglévő oldal
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 font-mono font-medium">
                  {page.records.length} pont
                </span>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {page.records.map((item) => (
                  <div key={item.id} className="p-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition flex items-start justify-between gap-4">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-800 px-2 py-0.5 rounded-md">
                          {item.pont || 'N/A'}
                        </span>
                        <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
                          {item.cim || '(Nincs cím)'}
                        </span>

                        {item.isIncomplete && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300 font-medium px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
                            <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                            <span>Hiányos kötelező adat</span>
                          </span>
                        )}

                        {item.existsInTarget && (
                          <span className="text-[10px] text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded-full font-medium" title="Ilyen pontszámú pont már van a szervezetnél – a választott stratégia szerint frissül vagy új pontként kerül be">
                            Létező pont
                          </span>
                        )}

                        {item.status === 'new' && (
                          <span className="text-[10px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full font-medium">
                            Új tétel
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                        {item.leiras || <span className="italic text-slate-400 dark:text-slate-600">Nincs szöveges leírás (leiras.txt hiányzott)</span>}
                      </p>

                      {item.attachments.length > 0 && (
                        <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                          <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Mellékletek:</span>
                          {item.attachments.map((att, aIdx) => (
                            <span key={aIdx} className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 flex items-center gap-1 font-medium">
                              <FileText className="w-3 h-3 text-blue-500" />
                              <span>{att.filename}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Műveleti gombok (Szerkesztés / Törlés) */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => {
                          setEditingItem({ pageTitle: page.pageTitle, item });
                          setEditForm({
                            pont: item.pont,
                            cim: item.cim,
                            leiras: item.leiras,
                            kulso_linkek: item.kulso_linkek,
                          });
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Tétel módosítása"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Törlés a feltöltési tervből"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tétel szerkesztő Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Staging Tétel Szerkesztése</h3>
              <button onClick={() => setEditingItem(null)} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Pont azonosító</label>
                <input
                  type="text"
                  value={editForm.pont}
                  onChange={(e) => setEditForm({ ...editForm, pont: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Cím / Megnevezés</label>
                <input
                  type="text"
                  value={editForm.cim}
                  onChange={(e) => setEditForm({ ...editForm, cim: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Szöveges leírás</label>
                <textarea
                  rows={4}
                  value={editForm.leiras}
                  onChange={(e) => setEditForm({ ...editForm, leiras: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setEditingItem(null)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-transparent transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-xs shadow-md transition cursor-pointer"
                >
                  Mentés a Staging tervbe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hiányzó tétel hozzáadása Modal */}
      {isAddingNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Hiányzó Tétel Pótlása</h3>
              <button onClick={() => setIsAddingNew(false)} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Céloldal</label>
                <select
                  value={newItemPage}
                  onChange={(e) => setNewItemPage(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                >
                  {stagingBatch?.pages.map((p, idx) => (
                    <option key={idx} value={p.pageTitle}>{p.pageTitle}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Pont azonosító</label>
                <input
                  type="text"
                  placeholder="pl. 1.3"
                  value={newItemForm.pont}
                  onChange={(e) => setNewItemForm({ ...newItemForm, pont: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Cím / Megnevezés</label>
                <input
                  type="text"
                  placeholder="Megnevezés..."
                  value={newItemForm.cim}
                  onChange={(e) => setNewItemForm({ ...newItemForm, cim: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Szöveges tartalom</label>
                <textarea
                  rows={3}
                  placeholder="Közzéteendő tartalom..."
                  value={newItemForm.leiras}
                  onChange={(e) => setNewItemForm({ ...newItemForm, leiras: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setIsAddingNew(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-transparent transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  onClick={handleSaveNewItem}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs shadow-md transition cursor-pointer"
                >
                  Tétel Hozzáadása
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
